import type { FunctionComponent } from 'react'
import { useEffect } from 'react'
import { useMemo } from 'react'
import { useEntityHistory } from '../useEntityHistory/index'
import filter from 'lodash/filter'
import find from 'lodash/find'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import type { Activity, FilterQuery, History } from '@ztechapps/common.contract'
import { gql } from '@apollo/client'

export const useActivityRemarksAndHistory = ({
  remarksActivity = [],
  historyFilter,
  component,
  showHistory,
}: {
  remarksActivity: Activity[]
  historyFilter?: FilterQuery<History>
  component: FunctionComponent<{ item: History }>
  showHistory?: boolean
}) => {
  const { list, subscribeToMore } = useEntityHistory({
    filter: historyFilter,
    limit: 0,
    options: { queryOptions: { fetchPolicy: 'network-only', skip: !showHistory || !historyFilter } },
  })

  useEffect(() => {
    if (showHistory) {
      subscribeToMore({
        document: gql(`
          subscription createdHistory($filter: JSON) {
            createdHistory(filter: $filter) {
              _id
              entity
              entityKey
              timeStamp
              description
              author
              diff
            }
          }
        `),
        variables: { filter: historyFilter },
        updateQuery: (prev, { subscriptionData }) => {
          if (!subscriptionData?.data) return prev

          const newHistory = subscriptionData.data.createdHistory

          return {
            ...prev,
            allHistory: [...prev.allHistory, newHistory],
          }
        },
      })
    }
  }, [subscribeToMore, showHistory])

  const historyList = filter(list, (item) => {
    const parsedDiff = JSON.parse(item.diff)
    if (parsedDiff?.length === 1 && parsedDiff[0]?.fieldName === 'viewCount') return false
    const noRemarksField = find(parsedDiff, (diff) => !diff.fieldName.startsWith('remarks'))

    if (isEmpty(noRemarksField)) return false

    return true
  })

  //render the activity as a react component
  const historyListAsActivity = map(historyList, (item) => {
    return {
      _id: item._id,
      activity: component({ item }),
      updatedAt: item.timeStamp,
      isDeleted: false,
    }
  }) as unknown as Activity[]

  const activities = useMemo(() => [...(remarksActivity ?? []), ...(historyListAsActivity ?? [])], [remarksActivity, historyListAsActivity])

  return { activities }
}
